/**
 * One operations promotion, fetched by id from the CRM Events `/campaigns/{id}`
 * endpoint (scoped to the active brand's tenant) for the promotion-detail page.
 *
 * Returns the item mapped onto a browser-card `Promotion` (via the shared
 * `campaign-map` mapper, so the detail resolves an id exactly as the list does)
 * plus the raw `detail` payload, whose detail-only fields (description / offering
 * / players_engaged) enrich the Running-promotion hero. `promotion` is null while
 * loading and when the id is unknown (a stale/expired link) so the page can show
 * its not-found state instead of a blank screen.
 */
import React from 'react';

import { useAppSelector } from '@/store';

import type { Promotion } from '../../mock-data/types';
import { getOperationsCampaign, type OperationsCampaignDetail } from '../operations/api/crm-events';
import { toPromotion } from '../operations/api/campaign-map';

export type OperationsPromotionResult = {
  promotion: Promotion | null;
  detail: OperationsCampaignDetail | null;
  loading: boolean;
};

export const useOperationsPromotion = (id: string | undefined): OperationsPromotionResult => {
  const tenantId = useAppSelector((state) => state.casinoInstance.displayBrand?.tenant_id);
  const [promotion, setPromotion] = React.useState<Promotion | null>(null);
  const [detail, setDetail] = React.useState<OperationsCampaignDetail | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!tenantId || !id) {
      setLoading(true);
      setPromotion(null);
      setDetail(null);
      return;
    }
    const controller = new AbortController();
    const { signal } = controller;
    setLoading(true);
    getOperationsCampaign(id, { tenantId }, signal)
      .then((response) => {
        if (signal.aborted) return;
        setDetail(response);
        setPromotion(toPromotion(response, 0));
        setLoading(false);
      })
      .catch(() => {
        if (signal.aborted) return;
        setDetail(null);
        setPromotion(null);
        setLoading(false);
      });
    return () => controller.abort();
  }, [tenantId, id]);

  return { promotion, detail, loading };
};

export default useOperationsPromotion;
